"use client";

import { usePathname, useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { locales, localeNames, type Locale } from "@/i18n/settings";

const RADIUS = "rounded-2xl";
const MAX_HEIGHT_DESKTOP = "max-h-[65vh]";
const MAX_HEIGHT_MOBILE = "max-h-[50vh]";

export function LanguageSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale() as Locale;
  const [isOpen, setIsOpen] = useState(false);

  const isAppPath = pathname.includes(`/${currentLocale}/app`);
  if (isAppPath) {
    return null;
  }

  const handleLanguageChange = (newLocale: Locale) => {
    console.log("Changing language to", newLocale);
    let newPath = pathname;

    if (locales.some((loc) => newPath.startsWith(`/${loc}`))) {
      newPath = newPath.replace(`/${currentLocale}`, `/${newLocale}`);
    } else {
      newPath = `/${newLocale}${newPath}`;
    }

    router.push(newPath);
    setIsOpen(false);
  };

  const formatLanguageDisplay = (locale: Locale) => (
    <div className="flex items-center justify-start w-full">
      <span className="text-lg">{localeNames[locale].flag}</span>
      <span className="ml-2 min-w-[24px]">{localeNames[locale].name}</span>
    </div>
  );

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            className={`
              ${isOpen ? "bg-surface" : "bg-surface-light"}
              hover:bg-surface
              ${RADIUS}
              text-surface-foreground
              border-0
              p-3
              transition-colors
              duration-200
              focus:outline-none
              shadow-lg
              backdrop-blur-md
              bg-opacity-90
              w-[90px]
            `}
          >
            {formatLanguageDisplay(currentLocale)}
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            className={`
              bg-surface-light
              ${RADIUS}
              p-1
              border-0
              shadow-lg
              animate-slideUpAndFade
              backdrop-blur-md
              bg-opacity-90
              ${MAX_HEIGHT_DESKTOP}
              md:${MAX_HEIGHT_MOBILE}
              overflow-y-auto
              scrollbar-thin
              scrollbar-thumb-surface
              scrollbar-track-transparent
              w-[90px]
              /* Scroll */
              scroll-smooth
              overscroll-contain
              /* Mobile */
              touch-pan-y
              -translate-y-4
              md:-translate-y-2
            `}
          >
            <div className="py-1">
              {locales.map((locale) => (
                <DropdownMenu.Item
                  key={locale}
                  onSelect={() => handleLanguageChange(locale)}
                  className={`
                  cursor-pointer
                    p-3
                    my-1
                    ${RADIUS}
                    focus:bg-surface
                    hover:bg-surface
                    data-[highlighted]:bg-surface
                    ${currentLocale === locale ? "opacity-50 bg-surface" : ""}
                    outline-none
                    transition-colors
                    duration-200
                    select-none
                `}
                  disabled={currentLocale === locale}
                >
                  {formatLanguageDisplay(locale)}
                </DropdownMenu.Item>
              ))}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
